import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { fetchAttachmentBlob } from "./todoApi";
import { copyToClipboard, errorText, formatBytes, formatDuration, userName, whenText } from "./todoUtils";

/*
 * Файл задачи: фото, голосовое с расшифровкой, видео или документ.
 * Файлы закрытые — браузер получает их запросом с токеном и показывает из памяти.
 */

/** Сколько уже открытых файлов держим в памяти (старые выгружаются). */
const CACHE_LIMIT = 40;
/** Видео больше этого в браузер не грузим — только скачивание. */
const VIDEO_INLINE_LIMIT = 300 * 1024 * 1024;

const cache = new Map();
const pending = new Map();

const remember = (key, url) => {
    cache.delete(key);
    cache.set(key, url);

    while (cache.size > CACHE_LIMIT) {
        const [oldKey, oldUrl] = cache.entries().next().value;
        cache.delete(oldKey);
        URL.revokeObjectURL(oldUrl);
    }
};

/** Адрес файла в памяти браузера. Один и тот же файл дважды не качаем. */
const loadFileUrl = (taskId, attachmentId) => {
    const key = `${taskId}:${attachmentId}`;
    if (cache.has(key)) return Promise.resolve(cache.get(key));
    if (pending.has(key)) return pending.get(key);

    const job = fetchAttachmentBlob(taskId, attachmentId)
        .then((blob) => {
            const url = URL.createObjectURL(blob);
            remember(key, url);
            return url;
        })
        .finally(() => pending.delete(key));

    pending.set(key, job);
    return job;
};

const saveBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename || "file";
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1500);
};

const fileErrorText = (error) => {
    const status = Number(error?.response?.status || 0);
    if (status === 404) return "Файл удалён или недоступен";
    if (status === 401 || status === 403) return "Нет доступа к файлу";
    return "Не удалось загрузить файл";
};

const KIND_ICONS = { voice: "🎤", video: "🎬", doc: "▤", image: "▣" };

/**
 * К какому файлу относится комментарий: мини-фото (или значок) и название.
 * По нажатию страница прокручивается к самому файлу. attachment = null —
 * файл уже удалили.
 */
export function TodoFileRef({ taskId, attachment }) {
    const [url, setUrl] = useState("");
    const isImage = attachment?.kind === "image";
    const fileId = attachment?._id || "";

    useEffect(() => {
        let cancelled = false;
        setUrl("");
        if (!isImage || !fileId) return undefined;

        // Тот же запас, что у самих файлов: фото второй раз не скачивается.
        loadFileUrl(taskId, fileId)
            .then((next) => {
                if (!cancelled) setUrl(next);
            })
            .catch(() => undefined);

        return () => {
            cancelled = true;
        };
    }, [fileId, isImage, taskId]);

    if (!attachment) {
        return <span className="todoFileRef is-missing">к файлу, который удалили</span>;
    }

    const name = attachment.originalName || "вложение";
    const label = attachment.kind === "voice" ? "к голосовому" : attachment.kind === "image" ? "к фото" : "к файлу";

    return (
        <button
            type="button"
            className="todoFileRef todoComment__file"
            title="Показать этот файл"
            onClick={(event) => {
                // Ищем файл в том же окне или панели, где стоит комментарий.
                const scope = event.currentTarget.closest(".todoDialog, .todoPanel") || document;
                scope.querySelector(`[data-file-id="${fileId}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
            }}
        >
            <span className="todoFileRef__thumb" aria-hidden="true">
                {url ? <img src={url} alt="" /> : KIND_ICONS[attachment.kind] || KIND_ICONS.doc}
            </span>
            <span className="todoFileRef__text">{label}: {name}</span>
        </button>
    );
}

/** Фото на весь экран. Закрывается нажатием и клавишей Esc. */
function ImageViewer({ url, name, onClose }) {
    useEffect(() => {
        const onKey = (event) => {
            if (event.key === "Escape") onClose();
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [onClose]);

    return createPortal(
        <div
            className="todoViewer"
            role="dialog"
            aria-modal="true"
            aria-label={name}
            onClick={(event) => {
                event.stopPropagation();
                onClose();
            }}
            onMouseDown={(event) => event.stopPropagation()}
            onPointerDown={(event) => event.stopPropagation()}
        >
            <img src={url} alt={name} />
            <button type="button" className="todoViewer__close" aria-label="Закрыть">✕</button>
        </div>,
        document.body
    );
}

export default function TodoAttachment({
    taskId,
    attachment,
    meId = "",
    /** Сколько комментариев написано к этому файлу. */
    commentCount = 0,
    /** Сейчас пишут комментарий именно к этому файлу. */
    commenting = false,
    canDelete = false,
    onComment = null,
    onDelete = null,
    onRetryTranscript = null,
}) {
    const kind = attachment.kind || "doc";
    const name = attachment.originalName || "Файл";
    const size = Number(attachment.sizeBytes || 0);

    const [url, setUrl] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [viewerOpen, setViewerOpen] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [copied, setCopied] = useState(false);
    const [busy, setBusy] = useState(false);

    const aliveRef = useRef(true);
    useEffect(() => {
        aliveRef.current = true;
        return () => {
            aliveRef.current = false;
        };
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        setError("");

        try {
            const next = await loadFileUrl(taskId, attachment._id);
            if (aliveRef.current) setUrl(next);
            return next;
        } catch (loadError) {
            if (aliveRef.current) setError(fileErrorText(loadError));
            return "";
        } finally {
            if (aliveRef.current) setLoading(false);
        }
    }, [attachment._id, taskId]);

    // Фото показываем сразу; голосовое и видео — когда нажмут.
    useEffect(() => {
        setUrl("");
        setError("");
        if (kind === "image") load();
    }, [attachment._id, kind, load]);

    useEffect(() => {
        if (!copied) return undefined;
        const timer = window.setTimeout(() => setCopied(false), 1500);
        return () => window.clearTimeout(timer);
    }, [copied]);

    const download = async () => {
        if (downloading) return;
        setDownloading(true);
        setError("");

        try {
            const blob = await fetchAttachmentBlob(taskId, attachment._id, { download: true });
            saveBlob(blob, name);
        } catch (downloadError) {
            if (aliveRef.current) setError(fileErrorText(downloadError));
        } finally {
            if (aliveRef.current) setDownloading(false);
        }
    };

    const runAction = async (action) => {
        if (!action || busy) return;
        setBusy(true);
        setError("");

        try {
            await action(attachment);
        } catch (actionError) {
            if (aliveRef.current) setError(errorText(actionError));
        } finally {
            if (aliveRef.current) setBusy(false);
        }
    };

    const transcript = String(attachment.transcriptText || "").trim();
    const transcriptStatus = String(attachment.transcriptStatus || "");
    const meta = [
        userName(attachment.uploadedBy, meId, ""),
        whenText(attachment.createdAt),
        formatBytes(size),
    ].filter(Boolean).join(" · ");

    let body = null;

    if (kind === "image") {
        body = url ? (
            <button
                type="button"
                className="todoFile__image"
                onClick={() => setViewerOpen(true)}
                title="Открыть на весь экран"
            >
                <img src={url} alt={name} loading="lazy" />
            </button>
        ) : (
            <div className="todoFile__placeholder">{loading ? "Загружаем фото…" : name}</div>
        );
    } else if (kind === "voice") {
        body = (
            <div className="todoFile__voice">
                {url ? (
                    <audio src={url} controls autoPlay preload="metadata" />
                ) : (
                    <button type="button" className="todoFile__play" onClick={load} disabled={loading}>
                        <span aria-hidden="true">▶</span>
                        {loading ? "Загружаем…" : `Голосовое${attachment.durationSec ? ` · ${formatDuration(attachment.durationSec)}` : ""}`}
                    </button>
                )}

                {transcriptStatus === "done" && transcript ? (
                    <div className="todoFile__transcript">
                        <p>{transcript}</p>
                        <button
                            type="button"
                            onClick={async () => setCopied(await copyToClipboard(transcript))}
                        >
                            {copied ? "Скопировано" : "Скопировать текст"}
                        </button>
                    </div>
                ) : transcriptStatus === "done" ? (
                    <div className="todoFile__note">Речь не распознана</div>
                ) : transcriptStatus === "pending" ? (
                    <div className="todoFile__note">Расшифровываем…</div>
                ) : onRetryTranscript ? (
                    <button
                        type="button"
                        className="todoFile__link"
                        disabled={busy}
                        onClick={() => runAction(onRetryTranscript)}
                    >
                        {transcriptStatus === "error" ? "Расшифровка не удалась · Повторить" : "Расшифровать в текст"}
                    </button>
                ) : null}
            </div>
        );
    } else if (kind === "video") {
        body = url ? (
            <video className="todoFile__video" src={url} controls autoPlay playsInline preload="metadata" />
        ) : size > VIDEO_INLINE_LIMIT ? (
            <div className="todoFile__doc">
                <span aria-hidden="true">🎬</span>
                <b>{name}</b>
                <small>Видео большое — его можно только скачать</small>
            </div>
        ) : (
            <button type="button" className="todoFile__play" onClick={load} disabled={loading}>
                <span aria-hidden="true">▶</span>
                {loading
                    ? "Загружаем видео…"
                    : `Видео${attachment.durationSec ? ` · ${formatDuration(attachment.durationSec)}` : ""}`}
            </button>
        );
    } else {
        body = (
            <button type="button" className="todoFile__doc" onClick={download} disabled={downloading} title="Скачать">
                <span aria-hidden="true">▤</span>
                <b>{name}</b>
                <small>{downloading ? "Скачиваем…" : "Нажмите, чтобы скачать"}</small>
            </button>
        );
    }

    return (
        <div className={`todoFile ${commenting ? "is-commenting" : ""}`} data-file-id={attachment._id}>
            {body}

            {error ? (
                <div className="todoFile__error" role="alert">
                    {error}
                    {kind !== "doc" ? <button type="button" onClick={load}>Повторить</button> : null}
                </div>
            ) : null}

            <div className="todoFile__foot">
                <span className="todoFile__meta" title={name}>
                    {kind === "image" || kind === "voice" ? meta : [name, meta].filter(Boolean).join(" · ")}
                </span>

                <span className="todoFile__actions">
                    {onComment ? (
                        <button type="button" onClick={() => onComment(attachment)} title="Написать комментарий к этому файлу">
                            💬{commentCount > 0 ? ` ${commentCount}` : ""}
                        </button>
                    ) : commentCount > 0 ? (
                        <span>💬 {commentCount}</span>
                    ) : null}
                    {kind !== "doc" ? (
                        <button type="button" onClick={download} disabled={downloading} title="Скачать">
                            {downloading ? "…" : "↓"}
                        </button>
                    ) : null}
                    {canDelete && onDelete ? (
                        <button
                            type="button"
                            className="is-danger"
                            disabled={busy}
                            onClick={() => {
                                if (window.confirm(`Удалить файл «${name}»?`)) runAction(onDelete);
                            }}
                            title="Удалить файл"
                        >
                            ✕
                        </button>
                    ) : null}
                </span>
            </div>

            {viewerOpen && url ? <ImageViewer url={url} name={name} onClose={() => setViewerOpen(false)} /> : null}
        </div>
    );
}
